import { jmolColors, covalentRadius } from "./atomicData";

function matVecMultiply(A, x) {
  // I don't know of any more elegant way to do this in JS...
  return [
    [A[0][0] * x[0] + A[0][1] * x[1] + A[0][2] * x[2]],
    [A[1][0] * x[0] + A[1][1] * x[1] + A[1][2] * x[2]],
    [A[2][0] * x[0] + A[2][1] * x[1] + A[2][2] * x[2]],
  ];
}

// An individual atom component in X3D
const X3DAtom = (atom) => {
  return (
    <transform translation={atom.pos.join(" ")}>
      <shape>
        <appearance>
          <material
            diffuseColor={atom.color.join(" ")}
            specularColor="0.5 0.5 0.5"
          />
        </appearance>
        <sphere radius={atom.radius} />
      </shape>
    </transform>
  );
};

class Atom {
  constructor(pos, fracPos, color, radius) {
    this.pos = pos;
    this.fracPos = fracPos;
    this.color = color;
    this.radius = radius;
  }

  distance(otherAtom) {
    return Math.sqrt(
      this.pos
        .map((c, i) => {
          return Math.pow(c - otherAtom.pos[i], 2);
        })
        .reduce((sum, x) => {
          return sum + x;
        }, 0),
    );
  }
}

// Converts structure to atomic positions/colors/radii
class AtomStructure {
  // Parse structure (currently in POSCAR format)  TODO: use better standard
  constructor(structureData, viewDistanceMult = 1.2) {
    this.viewDistanceMult = viewDistanceMult;
    this.scalingFactor = Number(structureData[1].trim());
    this.cartesian = structureData[7].trim().toLowerCase() == "Cartesian";
    // Read lattice vectors
    this.R_T = [2, 3, 4].map((i) => {
      return structureData[i]
        .trim()
        .split(/\s+/)
        .map((v) => {
          return this.scalingFactor * Number(v);
        });
    });
    // Transpose to get R
    this.R = [...Array(3).keys()].map((i) => {
      return [...Array(3).keys()].map((j) => {
        return this.R_T[j][i];
      });
    });
    let elements = structureData[5].trim().split(/\s+/);
    let elementCounts = structureData[6]
      .trim()
      .split(/\s+/)
      .map((v) => {
        return Number(v);
      });
    this.species = elements.map((v, i) => [v, elementCounts[i]]);

    // Get atomic positions
    this.positions = [...Array(structureData.length).keys()]
      .slice(8)
      .map((i) => {
        return structureData[i]
          .trim()
          .split(/\s+/)
          .map((v) => {
            return Number(v);
          })
          .slice(0, 3);
      });

    // Get moveScale
    this.moveScale = [...Array(structureData.length).keys()]
      .slice(8)
      .map((i) => {
        return structureData[i]
          .trim()
          .split(/\s+/)
          .map((v) => {
            return Number(v);
          })
          .slice(3);
      });
    this.N = this.positions.length;

    // Now that data is obtained from file, create a set of Atom objects
    // with the necessary data to create X3D components.
    let atomIndex = 0;
    this.atoms = new Array();
    let cartesianPos = new Array();
    // Go through each species
    for (const [atomSymbol, count] of this.species) {
      for (const i of Array(count).keys()) {
        if (this.cartesian) {
          var pos = this.positions[atomIndex].map((i) => {
            return this.scalingFactor * i;
          });
          var fracPos = null;
        } else {
          var pos = matVecMultiply(this.R, this.positions[atomIndex]);
          var fracPos = this.positions[atomIndex].map((i) => {
            return this.scalingFactor * i;
          });
        }
        cartesianPos.push(pos);
        pos = pos.map((i) => {
          return Number(i).toFixed(16);
        });
        let color = jmolColors[atomSymbol].map((i) => {
          return Number(i / 255.0).toFixed(3);
        });
        let radius = Number(covalentRadius[atomSymbol]).toFixed(3);
        this.atoms.push(new Atom(pos, fracPos, color, radius));
        atomIndex += 1;
      }
    }

    // Calculate bounding box
    this.bbox_min = [...Array(3).keys()].map((i) => {
      return Math.min(
        ...cartesianPos.map((site) => {
          return site[i];
        }),
      );
    });
    this.bbox_max = [...Array(3).keys()].map((i) => {
      return Math.max(
        ...cartesianPos.map((site) => {
          return site[i];
        }),
      );
    });

    // Calculate origin and view distance
    this.origin = [...Array(3).keys()].map((i) => {
      return 0.5 * (this.bbox_min[i] + this.bbox_max[i]);
    });
    let box_size = [...Array(3).keys()].map((i) => {
      return this.bbox_max[i] - this.bbox_min[i];
    });
    this.viewDistance =
      this.viewDistanceMult *
      Math.sqrt(box_size[0] ** 2 + box_size[1] ** 2 + box_size[2] ** 2);
  }

  similarity(otherStructure) {
    if (otherStructure.N != this.N) {
      return Infinity;
    }
    return this.atoms
      .map((atom, i) => {
        return atom.distance(otherStructure.atoms[i]);
      })
      .reduce((sum, x) => {
        return sum + x;
      }, 0);
  }

  getScene() {
    // Return atoms and a calculated viewpoint as a list of React components
    return this.atoms
      .map((atom) => {
        return X3DAtom(atom);
      })
      .concat([
        <viewpoint
          position={
            this.origin[0].toFixed(2) +
            " " +
            this.origin[1].toFixed(2) +
            " " +
            (this.origin[2] + this.viewDistance).toFixed(2)
          }
          centerOfRotation={
            this.origin[0].toFixed(2) +
            " " +
            this.origin[1].toFixed(2) +
            " " +
            this.origin[2].toFixed(2)
          }
          isactive={"true"}
        />,
      ]);
  }
}

export { AtomStructure };
